import { supabase } from '../lib/supabase';

/**
 * Debug function to manually compare first Instagram reel with all file creatives
 * for the same artist and release
 */
export async function debugCompareFirstInstagramReel(artistId: string): Promise<void> {
  console.log('=== DEBUG: Starting manual comparison ===');
  console.log('Artist ID:', artistId);

  try {
    // Get first Instagram reel for this artist
    console.log('Fetching first Instagram reel for the artist');
    const { data: igCreative, error: igError } = await supabase
      .from('ad_creatives')
      .select('id, instagram_thumbnail_url, release_id, artists_id, merged_instagram_reel_url')
      .eq('platform' as any, 'instagram')
      .eq('artists_id' as any, artistId as any)
      .not('instagram_thumbnail_url', 'is', null)
      .limit(1)
      .single();

    console.log('Instagram query result:', { igCreative, igError });

    if (igError) throw igError;
    if (!igCreative) {
      console.log('No Instagram creatives found for this artist');
      return;
    }

    console.log('\n=== Instagram Reel ===');
    console.log('ID:', (igCreative as any).id);
    console.log('Release ID:', (igCreative as any).release_id);
    console.log('Thumbnail:', (igCreative as any).instagram_thumbnail_url);

    if (!(igCreative as any).instagram_thumbnail_url) {
      console.log('No thumbnail URL for Instagram reel, skipping');
      return;
    }

    // Check if Instagram creative has a release_id
    if (!(igCreative as any).release_id) {
      console.log('Instagram reel has no release_id, cannot compare with file creatives');
      return;
    }

    // Get all file creatives for same artist and release
    console.log('\nFetching file creatives...');
    const { data: fileCreatives, error: fileError } = await supabase
      .from('ad_creatives')
      .select('id, content, platform, thumbnail_url, video_name, release_id, artists_id')
      .eq('artists_id' as any, artistId as any)
      .eq('release_id' as any, (igCreative as any).release_id as any)
      .in('platform' as any, ['direct_upload', 'dropbox'] as any);

    console.log('File creatives query result:', { fileCreatives, fileError });

    if (fileError) throw fileError;
    if (!fileCreatives || fileCreatives.length === 0) {
      console.log('No file creatives found for same artist and release');
      return;
    }

    console.log('\n=== File Creatives to Compare ===');
    console.log('Total file creatives:', fileCreatives.length);

    // Compare with each file creative
    for (let i = 0; i < fileCreatives.length; i++) {
      const fileCreative = fileCreatives[i];
      console.log(`\n--- Comparison ${i + 1}/${fileCreatives.length} ---`);
      console.log('File ID:', (fileCreative as any).id);
      console.log('File Name:', (fileCreative as any).video_name);
      console.log('Available URL fields:');
      console.log('  - content:', (fileCreative as any).content);
      console.log('  - thumbnail_url:', (fileCreative as any).thumbnail_url);
      
      // Use thumbnail_url for comparison (content field contains video URLs which can't be loaded as images)
      const fileImageUrl = (fileCreative as any).thumbnail_url;
      console.log('Selected File Image URL:', fileImageUrl);

      if (!fileImageUrl) {
        console.log('No image URL for file creative, skipping');
        continue;
      }

      // Compare images
      console.log('Starting image comparison...');
      const similarity = await compareImageSimilarity(
        (igCreative as any).instagram_thumbnail_url,
        fileImageUrl
      );

      console.log('Similarity:', (similarity * 100).toFixed(2) + '%');
      console.log('Above threshold (99.5%)?', similarity > 0.995 ? 'YES' : 'NO');
    }

    console.log('\n=== DEBUG: Comparison complete ===');
  } catch (error) {
    console.error('Error in debug comparison:', error);
    throw error;
  }
}

interface CreativeMatch {
  fileCreative: any;
  instagramCreative: any;
  similarity: number;
}

/**
 * Compare thumbnails between file uploads and Instagram reels to detect duplicates
 * Uses improved perceptual hash comparison for image similarity
 * @param creativeIds - Optional list of specific creative IDs to check (if not provided, checks all for artist)
 */
export async function detectDuplicateCreatives(artistId: string, creativeIds?: string[]): Promise<CreativeMatch[]> {
  try {
    // Fetch creatives for this artist
    let query = supabase
      .from('ad_creatives')
      .select('id, content, platform, thumbnail_url, instagram_thumbnail_url, video_name, release_id, artists_id, merged_instagram_reel_url, original_instagram_url')
      .eq('artists_id' as any, artistId as any)
      .neq('status' as any, 'archived' as any);

    // If specific creative IDs provided, filter to those
    if (creativeIds && creativeIds.length > 0) {
      query = query.in('id' as any, creativeIds as any);
    }

    const { data: creatives, error } = await query;

    if (error) throw error;
    if (!creatives || creatives.length === 0) return [];

    console.log(`🔍 Duplicate detection: Found ${creatives.length} total creatives for artist ${artistId}`);

    // Log platform breakdown
    const platformCounts = creatives.reduce((acc: any, c: any) => {
      acc[c.platform] = (acc[c.platform] || 0) + 1;
      return acc;
    }, {});
    console.log(`🔍 Platform breakdown:`, platformCounts);

    // Separate file uploads and Instagram reels
    const fileCreatives = creatives.filter((c: any) =>
      (c.platform === 'direct_upload' || c.platform === 'dropbox') &&
      // Exclude file creatives that already have merged Instagram URL (already combined)
      !(c as any).merged_instagram_reel_url
    );
    const instagramCreatives = creatives.filter((c: any) => c.platform === 'instagram');

    const mergedFileCreatives = creatives.filter((c: any) =>
      (c.platform === 'direct_upload' || c.platform === 'dropbox') &&
      (c as any).merged_instagram_reel_url
    );

    console.log(`🔍 File uploads (eligible): ${fileCreatives.length}, Instagram creatives: ${instagramCreatives.length}, Merged file uploads: ${mergedFileCreatives.length}`);

    // Log thumbnail availability
    const fileCreativesWithThumbnails = fileCreatives.filter((c: any) => (c as any).thumbnail_url);
    const instagramCreativesWithThumbnails = instagramCreatives.filter((c: any) =>
      (c as any).instagram_thumbnail_url || (c as any).thumbnail_url
    );

    console.log(`🔍 File uploads with thumbnails: ${fileCreativesWithThumbnails.length}/${fileCreatives.length}`);
    console.log(`🔍 Instagram creatives with thumbnails: ${instagramCreativesWithThumbnails.length}/${instagramCreatives.length}`);

    if (fileCreativesWithThumbnails.length === 0 && instagramCreativesWithThumbnails.length === 0) {
      console.warn('⚠️ No creatives with thumbnails found - duplicate detection requires thumbnails');
    }

    const matches: CreativeMatch[] = [];
    const matchedInstagramIds = new Set<string>();
    const matchedFileIds = new Set<string>();

    // Compare each Instagram reel with each file upload
    for (const igCreative of instagramCreatives) {
      // Use instagram_thumbnail_url if available, otherwise fall back to thumbnail_url (for replaced files)
      const igImageUrl = (igCreative as any).instagram_thumbnail_url || (igCreative as any).thumbnail_url;
      if (!igImageUrl) continue;
      if (matchedInstagramIds.has((igCreative as any).id)) continue;

      let bestMatch: any = null;
      let bestSimilarity = 0;
      let comparisonsMade = 0;

      for (const fileCreative of fileCreatives) {
        // Use thumbnail_url for comparison (content field contains video URLs which can't be loaded as images)
        const fileImageUrl = (fileCreative as any).thumbnail_url;
        if (!fileImageUrl) continue;
        if (matchedFileIds.has((fileCreative as any).id)) continue;

        // Calculate similarity between thumbnails
        const similarity = await compareImageSimilarity(
          igImageUrl,
          fileImageUrl
        );
        comparisonsMade++;

        // Keep track of the best match for this Instagram creative
        if (similarity > bestSimilarity) {
          bestSimilarity = similarity;
          bestMatch = fileCreative;
        }
      }

      console.log(`🔍 IG creative ${igCreative.id}: compared with ${comparisonsMade} file uploads, best similarity: ${(bestSimilarity * 100).toFixed(1)}%`);

      // If best match has high similarity (>75%), add it and mark as matched
      if (bestMatch && bestSimilarity > 0.75) {
        matches.push({
          fileCreative: bestMatch,
          instagramCreative: igCreative,
          similarity: bestSimilarity
        });
        matchedInstagramIds.add((igCreative as any).id);
        matchedFileIds.add((bestMatch as any).id);
      }
    }

    // Compare each file creative with other file creatives (file vs file duplicates)
    for (let i = 0; i < fileCreatives.length; i++) {
      const fileCreative1 = fileCreatives[i];
      const fileImageUrl1 = (fileCreative1 as any).thumbnail_url;
      if (!fileImageUrl1) continue;
      if (matchedFileIds.has((fileCreative1 as any).id)) continue;

      let bestMatch: any = null;
      let bestSimilarity = 0;
      let comparisonsMade = 0;

      for (let j = i + 1; j < fileCreatives.length; j++) {
        const fileCreative2 = fileCreatives[j];
        const fileImageUrl2 = (fileCreative2 as any).thumbnail_url;
        if (!fileImageUrl2) continue;
        if (matchedFileIds.has((fileCreative2 as any).id)) continue;

        // Calculate similarity between thumbnails
        const similarity = await compareImageSimilarity(
          fileImageUrl1,
          fileImageUrl2
        );
        comparisonsMade++;

        // Keep track of the best match for this file creative
        if (similarity > bestSimilarity) {
          bestSimilarity = similarity;
          bestMatch = fileCreative2;
        }
      }

      console.log(`🔍 File creative ${fileCreative1.id}: compared with ${comparisonsMade} other files, best similarity: ${(bestSimilarity * 100).toFixed(1)}%`);

      // If best match has high similarity (>75%), add it and mark as matched
      if (bestMatch && bestSimilarity > 0.75) {
        matches.push({
          fileCreative: fileCreative1,
          instagramCreative: bestMatch, // Using instagramCreative field for second file creative
          similarity: bestSimilarity
        });
        matchedFileIds.add((fileCreative1 as any).id);
        matchedFileIds.add((bestMatch as any).id);
      }
    }

    // Compare each Instagram creative with other Instagram creatives (IG vs IG duplicates)
    for (let i = 0; i < instagramCreatives.length; i++) {
      const igCreative1 = instagramCreatives[i];
      const igImageUrl1 = (igCreative1 as any).instagram_thumbnail_url || (igCreative1 as any).thumbnail_url;
      if (!igImageUrl1) continue;
      if (matchedInstagramIds.has((igCreative1 as any).id)) continue;

      let bestMatch: any = null;
      let bestSimilarity = 0;
      let comparisonsMade = 0;

      for (let j = i + 1; j < instagramCreatives.length; j++) {
        const igCreative2 = instagramCreatives[j];
        const igImageUrl2 = (igCreative2 as any).instagram_thumbnail_url || (igCreative2 as any).thumbnail_url;
        if (!igImageUrl2) continue;
        if (matchedInstagramIds.has((igCreative2 as any).id)) continue;

        // Calculate similarity between thumbnails
        const similarity = await compareImageSimilarity(
          igImageUrl1,
          igImageUrl2
        );
        comparisonsMade++;

        // Keep track of the best match for this Instagram creative
        if (similarity > bestSimilarity) {
          bestSimilarity = similarity;
          bestMatch = igCreative2;
        }
      }

      console.log(`🔍 IG creative ${igCreative1.id}: compared with ${comparisonsMade} other IG creatives, best similarity: ${(bestSimilarity * 100).toFixed(1)}%`);

      // If best match has high similarity (>75%), add it and mark as matched
      if (bestMatch && bestSimilarity > 0.75) {
        matches.push({
          fileCreative: igCreative1,
          instagramCreative: bestMatch,
          similarity: bestSimilarity
        });
        matchedInstagramIds.add((igCreative1 as any).id);
        matchedInstagramIds.add((bestMatch as any).id);
      }
    }

    console.log(`🔍 Duplicate detection complete: found ${matches.length} matches (>75% similarity)`);

    // Sort matches by similarity (highest to lowest)
    matches.sort((a, b) => b.similarity - a.similarity);

    return matches;
  } catch (error) {
    console.error('Error detecting duplicate creatives:', error);
    return [];
  }
}

/**
 * Compare two image URLs and return a similarity score (0-1)
 * Uses pixel-level difference calculation for maximum accuracy
 */
async function compareImageSimilarity(url1: string, url2: string): Promise<number> {
  try {
    console.log('Comparing images:', url1, url2);
    
    // Download images
    const img1 = await loadImage(url1);
    const img2 = await loadImage(url2);
    
    console.log('Images loaded successfully');

    // Calculate pixel-level difference
    const pixelDifference = calculatePixelDifference(img1, img2);
    
    console.log('Pixel difference:', pixelDifference);
    
    // Convert to similarity score (0-1)
    const similarity = 1 - pixelDifference;
    
    console.log('Similarity:', similarity);
    
    return similarity;
  } catch (error) {
    console.error('Error comparing images:', error);
    return 0;
  }
}

/**
 * Load an image from URL and return ImageData
 */
async function loadImage(url: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Resize to higher resolution for better accuracy (128x128)
      canvas.width = 128;
      canvas.height = 128;
      ctx.drawImage(img, 0, 0, 128, 128);

      const imageData = ctx.getImageData(0, 0, 128, 128);
      resolve(imageData);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/**
 * Calculate pixel-level difference between two images
 * Returns a value between 0 (identical) and 1 (completely different)
 */
function calculatePixelDifference(img1: ImageData, img2: ImageData): number {
  const data1 = img1.data;
  const data2 = img2.data;
  const totalPixels = img1.width * img1.height;
  
  let totalDifference = 0;
  
  for (let i = 0; i < totalPixels; i++) {
    const r1 = data1[i * 4];
    const g1 = data1[i * 4 + 1];
    const b1 = data1[i * 4 + 2];
    
    const r2 = data2[i * 4];
    const g2 = data2[i * 4 + 1];
    const b2 = data2[i * 4 + 2];
    
    // Calculate RGB difference
    const rDiff = Math.abs(r1 - r2) / 255;
    const gDiff = Math.abs(g1 - g2) / 255;
    const bDiff = Math.abs(b1 - b2) / 255;
    
    // Average RGB difference for this pixel
    totalDifference += (rDiff + gDiff + bDiff) / 3;
  }
  
  // Return average difference across all pixels
  return totalDifference / totalPixels;
}

/**
 * Merge two creatives by combining Instagram URL into the file creative
 * and deleting the Instagram creative
 */
export async function mergeCreatives(fileCreativeId: string, instagramCreativeId: string): Promise<void> {
  console.log('🔄 mergeCreatives called - fileCreativeId:', fileCreativeId, 'instagramCreativeId:', instagramCreativeId);
  try {
    // Fetch Instagram creative data with additional fields
    const { data: igCreative, error: igError } = await supabase
      .from('ad_creatives')
      .select('merged_instagram_reel_url, instagram_thumbnail_url, instagram_url_updated_at, audio_type, content, platform')
      .eq('id' as any, instagramCreativeId as any)
      .single();

    console.log('📥 Instagram creative fetched:', igCreative);
    if (igError) throw igError;
    if (!igCreative) throw new Error('Instagram creative not found');

    // Update file creative with Instagram URL
    const mergedUrl = (igCreative as any).merged_instagram_reel_url || (igCreative as any).content || null;
    console.log('📤 Setting merged_instagram_reel_url to:', mergedUrl);
    
    const { error: updateError } = await supabase
      .from('ad_creatives')
      .update({
        merged_instagram_reel_url: mergedUrl,
        instagram_thumbnail_url: (igCreative as any).instagram_thumbnail_url || null,
        instagram_url_updated_at: (igCreative as any).instagram_url_updated_at || null,
      } as any)
      .eq('id' as any, fileCreativeId as any);

    console.log('📝 File creative update result:', { error: updateError });
    if (updateError) throw updateError;

    // Archive Instagram creative instead of deleting
    const { error: archiveError } = await supabase
      .from('ad_creatives')
      .update({ status: 'archived' } as any)
      .eq('id' as any, instagramCreativeId as any);

    console.log('🗑️ Instagram creative archive result:', { error: archiveError });
    if (archiveError) throw archiveError;
    
    console.log('✅ Merge completed successfully');
  } catch (error) {
    console.error('❌ Error merging creatives:', error);
    throw error;
  }
}
